import {
    ApartmentOutlined,
    BadgeOutlined,
    CreditCardOutlined,
    DirectionsCarOutlined,
    InventoryOutlined,
    ManageAccountsOutlined,
} from '@mui/icons-material';
import {FC, ReactNode, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTone} from 'src/components/common/StatsStrip/StatsStrip.types';
import {TagOwnerType} from 'src/types/tags';
import {TagStatsStripType} from './TagStatsStrip.types';

/**
 * Whose card it is, by the holder's account type: a manager's own card is the business's, not a
 * private driver's. The links are not exclusive, so the tiles need not add up to the total.
 */
const STRIP_TYPES: TagOwnerType[] = ['DRIVER', 'COMPANY', 'EMPLOYEE', 'MANAGER', 'UNASSIGNED'];

/** The ids the list's own owner filter uses, so the two never name the same thing differently. */
const TYPE_LABELS: Record<TagOwnerType, string> = {
    DRIVER: 'privateUserTabName',
    COMPANY: 'companyTabForRfidDialog',
    EMPLOYEE: 'employee',
    MANAGER: 'managerTabForRfidDialog',
    UNASSIGNED: 'unassigned',
};

const TYPE_ICONS: Record<TagOwnerType, ReactNode> = {
    DRIVER: <DirectionsCarOutlined/>,
    COMPANY: <ApartmentOutlined/>,
    EMPLOYEE: <BadgeOutlined/>,
    MANAGER: <ManageAccountsOutlined/>,
    UNASSIGNED: <InventoryOutlined/>,
};

const TYPE_TONES: Record<TagOwnerType, StatsStripTone> = {
    DRIVER: 'primary',
    COMPANY: 'secondary',
    EMPLOYEE: 'success',
    MANAGER: 'brand',
    // Stock, not a problem — the neutral tone says "nothing wrong here, just not handed out yet".
    UNASSIGNED: 'neutral',
};

export const TagStatsStrip: FC<TagStatsStripType> = ({
                                                         data,
                                                         activeOwnerType,
                                                         onSelectOwnerType,
                                                         activeFlag,
                                                         onSelectFlag,
                                                     }) => {
    const intl = useIntl();

    const counts = useMemo(
        () => new Map((data?.types ?? []).map((point) => [point.type, point.tags])),
        [data]
    );

    return (
        <StatsStrip
            icon={<CreditCardOutlined/>}
            title="rfidPageHeader"
            subtitle={intl.formatMessage({id: 'tagStatsSubtitle'}, {tags: data?.tags ?? 0})}
            // The flags cut across the owners: a blocked card cannot charge, and a master card opens
            // every station in the business, whoever is holding it.
            pills={[
                {
                    key: 'blocked',
                    label: 'blocked',
                    value: data?.blocked,
                    tone: 'danger',
                    active: activeFlag === 'blocked',
                    onClick: onSelectFlag ? () => onSelectFlag('blocked') : undefined,
                },
                {
                    key: 'master',
                    label: 'tagsMaster',
                    value: data?.master,
                    tone: 'warning',
                    active: activeFlag === 'master',
                    onClick: onSelectFlag ? () => onSelectFlag('master') : undefined,
                },
            ]}
            tiles={STRIP_TYPES.map((type) => ({
                key: type,
                label: TYPE_LABELS[type],
                value: counts.get(type),
                icon: TYPE_ICONS[type],
                tone: TYPE_TONES[type],
                active: activeOwnerType === type,
                onClick: onSelectOwnerType ? () => onSelectOwnerType(type) : undefined,
            }))}
        />
    );
};
